import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import styles from './InfoTooltip.module.css';

const TOOLTIP_WIDTH = 320;
const EDGE_PADDING   = 12;
// Enough room above the icon for a bubble, or it opens downwards instead.
// A guess at the tall end, so a long tooltip in a table header doesn't get
// placed above and then clipped by the top of the window.
const FLIP_THRESHOLD = 180;

function InfoTooltip({ title, text }) {
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);

  const handleMouseEnter = () => {
    if (!wrapRef.current) return;
    const rect     = wrapRef.current.getBoundingClientRect();
    const idealCenter = rect.left + rect.width / 2;

    // left edge of tooltip if perfectly centred
    const rawLeft  = idealCenter - TOOLTIP_WIDTH / 2;
    // clamp so tooltip stays within viewport
    const clampedLeft = Math.max(
      EDGE_PADDING,
      Math.min(window.innerWidth - TOOLTIP_WIDTH - EDGE_PADDING, rawLeft)
    );
    // caret sits at the icon centre, expressed as % within the tooltip
    const caretPct = ((idealCenter - clampedLeft) / TOOLTIP_WIDTH) * 100;
    const below = rect.top < FLIP_THRESHOLD;

    setPos({
      below,
      bottom:   below ? undefined : window.innerHeight - rect.top + 10,
      top:      below ? rect.bottom + 10 : undefined,
      left:     clampedLeft,
      caretPct: Math.max(8, Math.min(92, caretPct)), // keep caret inside bubble
    });
  };

  /* The bubble is portalled to <body> with viewport coordinates, so it doesn't
     travel with the icon once the page moves — it would hang over unrelated
     content. On a phone that's the common case: a tap opens it and there's no
     pointer to leave, so scrolling is how you dismiss it. */
  useEffect(() => {
    if (!pos) return undefined;
    const close = () => setPos(null);
    // Capture, to catch scrolling inside a container as well as the page.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos]);

  return (
    /* A span, not a div, so the icon can sit inside phrasing content — the
       trade copier puts one inside a sortable column header's button. The
       click is swallowed for the same reason: reaching for the tooltip
       shouldn't re-sort the table behind it. */
    <span
      ref={wrapRef}
      className={styles.wrap}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setPos(null)}
      onClick={e => { e.preventDefault(); e.stopPropagation(); }}
    >
      <Info size={14} className={styles.icon} strokeWidth={2.5} />
      {pos && createPortal(
        <div
          className={styles.tooltip}
          style={{ bottom: pos.bottom, top: pos.top, left: pos.left }}
        >
          {title && <p className={styles.title}>{title}</p>}
          <p className={styles.text}>{text}</p>
          <span
            className={`${styles.caret} ${pos.below ? styles.caretTop : ''}`}
            style={{ left: `${pos.caretPct}%` }}
          />
        </div>,
        document.body
      )}
    </span>
  );
}

export default InfoTooltip;
